import {
  LayoutDashboard,
  Target,
  CalendarClock,
  Flame,
  Shield,
  Sparkles,
  Trophy,
  Medal,
  Goal,
  BarChart3,
  TrendingUp,
  Eye,
  History,
  Compass,
  BookOpen,
  Newspaper,
  Info,
  HelpCircle,
  Users,
  ShieldAlert,
  Brain,
  Mail,
  Plug,
  Settings2,
  Coins,
  type LucideIcon,
} from "lucide-react";

export interface NavLeaf {
  title: string;
  url: string;
  /** Short blurb shown in the desktop mega-menu. */
  description?: string;
}

export interface NavGroup {
  label: string;
  icon: LucideIcon;
  /** Optional landing route for the group header itself. */
  url?: string;
  items: NavLeaf[];
}

/**
 * Single source of truth for the whole navigation surface — desktop mega-menu,
 * sidebar and footer all read this. Every public route is linked here so the
 * site is fully reachable and crawlable by Google, ChatGPT, Claude and
 * Perplexity.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Predictions",
    icon: Target,
    url: "/predictions",
    items: [
      { title: "Dashboard", url: "/", description: "Today's picks & recent winners" },
      { title: "All Predictions", url: "/predictions", description: "Every upcoming football pick" },
      { title: "Today's Picks", url: "/predictions/today", description: "Games kicking off today" },
      { title: "Tomorrow", url: "/predictions/tomorrow", description: "Tomorrow's fixtures" },
      { title: "Weekend", url: "/predictions/weekend", description: "This weekend's slate" },
      { title: "Bonus Picks", url: "/predictions/bonus-picks-today", description: "Highest-confidence picks" },
      { title: "High Odds", url: "/high-odds", description: "Value at 7.00+ odds" },
      { title: "Safe Bets", url: "/safe-bets", description: "Low-risk, high-probability" },
      { title: "AI Predictions", url: "/ai-predictions", description: "AI-enhanced analysis" },
      { title: "Jackpot", url: "/jackpot-predictions", description: "Accumulator & jackpot tips" },
      // Fixture entry points live in the primary Predictions menu (not only under
      // Insights) so every /fixtures/:slug page is reachable by crawl, not just
      // via the sitemap.
      { title: "Fixtures Archive", url: "/fixtures", description: "Every past & upcoming fixture" },
      { title: "Match Previews", url: "/match-preview", description: "Fixture deep-dives" },
    ],
  },
  {
    label: "Sports",
    icon: Medal,
    items: [
      { title: "Football / Soccer", url: "/predictions", description: "The flagship sport" },
      { title: "NFL", url: "/nfl-predictions", description: "Moneyline, spread & totals" },
      { title: "NBA", url: "/nba-predictions", description: "Moneyline, spread & totals" },
      { title: "MLB", url: "/mlb-predictions", description: "Moneyline, run line & totals" },
      { title: "NHL", url: "/nhl-predictions", description: "Moneyline, puck line & totals" },
      { title: "NCAAF", url: "/ncaaf-predictions", description: "College football picks" },
      { title: "NCAAB", url: "/ncaab-predictions", description: "College basketball picks" },
      { title: "WNBA", url: "/wnba-predictions", description: "Moneyline, spread & totals" },
      { title: "MMA", url: "/mma-predictions", description: "Fight winner value" },
      { title: "Boxing", url: "/boxing-predictions", description: "Bout winner value" },
      { title: "AFL", url: "/afl-predictions", description: "Aussie rules picks" },
      { title: "NRL", url: "/nrl-predictions", description: "Rugby league picks" },
      { title: "Cricket (IPL)", url: "/ipl-predictions", description: "Match winner value" },
      { title: "Tennis (Slams)", url: "/tennis-atp-wimbledon-predictions", description: "ATP & WTA match winners" },
      { title: "Golf (Majors)", url: "/masters-predictions", description: "Outright winner value" },
      { title: "All Sports", url: "/explore", description: "Browse every sport hub" },
    ],
  },
  {
    label: "Competitions",
    icon: Trophy,
    url: "/competitions",
    items: [
      { title: "All Competitions", url: "/competitions", description: "Every league, cup & tournament" },
      { title: "By Country", url: "/countries", description: "Browse predictions by country" },
      { title: "All Leagues", url: "/leagues", description: "Every league hub" },
      { title: "Premier League", url: "/leagues/epl" },
      { title: "La Liga", url: "/leagues/la-liga" },
      { title: "Serie A", url: "/leagues/serie-a" },
      { title: "Bundesliga", url: "/leagues/bundesliga" },
      { title: "Ligue 1", url: "/leagues/ligue-1" },
      { title: "Championship", url: "/leagues/championship" },
      { title: "MLS", url: "/leagues/mls" },
      { title: "Champions League", url: "/leagues/champions-league" },
      { title: "Europa League", url: "/leagues/europa-league" },
      { title: "Conference League", url: "/competitions/conference-league" },
      { title: "Copa Libertadores", url: "/competitions/copa-libertadores" },
      { title: "Copa Sudamericana", url: "/competitions/copa-sudamericana" },
      { title: "FIFA World Cup 2026", url: "/leagues/world-cup" },
    ],
  },
  {
    label: "Markets",
    icon: Goal,
    url: "/markets",
    items: [
      { title: "All Markets", url: "/markets", description: "Browse every betting market" },
      { title: "Over/Under", url: "/markets/over-under" },
      { title: "BTTS", url: "/markets/btts" },
      { title: "Correct Score", url: "/markets/correct-score" },
      { title: "1X2", url: "/markets/1x2" },
      { title: "Double Chance", url: "/markets/double-chance" },
      { title: "Draw No Bet", url: "/markets/draw-no-bet" },
      { title: "Over 2.5 Goals", url: "/markets/over-2-5" },
      { title: "HT/FT", url: "/markets/htft" },
      { title: "Accumulator", url: "/markets/accumulator" },
    ],
  },
  {
    label: "Insights",
    icon: BarChart3,
    items: [
      { title: "Live Room", url: "/live", description: "Live scores & match intelligence" },
      { title: "Analysis & Live Odds", url: "/analysis", description: "Live odds and deep analysis" },
      { title: "Performance", url: "/performance", description: "Model accuracy dashboard" },
      { title: "Track Record", url: "/track-record", description: "Verified results history" },
      { title: "Match Preview", url: "/match-preview", description: "Fixture deep-dives" },
      { title: "Fixtures Archive", url: "/fixtures", description: "Every past & upcoming fixture" },
      { title: "Historical Odds", url: "/historical-odds", description: "Odds movement archive" },
      { title: "Player Impact", url: "/players", description: "How players shape our picks" },
      { title: "Explore Hub", url: "/explore", description: "Browse everything" },
    ],
  },
  {
    label: "Learn",
    icon: BookOpen,
    items: [
      { title: "Betting Guides", url: "/guides", description: "How to bet smarter" },
      { title: "News", url: "/blog/news", description: "Statlign newsroom" },
      { title: "Blog", url: "/blog", description: "Long-form analysis" },

      { title: "How It Works", url: "/how-it-works", description: "Our methodology" },
      { title: "FAQ", url: "/faq", description: "Common questions" },
      { title: "Founding Members", url: "/member", description: "Join the program" },
      { title: "Responsible Gambling", url: "/responsible-gambling", description: "Bet responsibly" },
    ],
  },
  {
    label: "Company",
    icon: Info,
    items: [
      { title: "About", url: "/about" },
      { title: "Contact", url: "/contact" },
      { title: "Agent API (MCP)", url: "/docs/mcp" },
      { title: "Privacy", url: "/privacy" },
      { title: "Terms", url: "/terms" },
      { title: "Settings", url: "/settings" },
    ],
  },
];

/** Per-item icons for the compact sidebar (keeps the mega-menu text-only). */
export const NAV_ICONS: Record<string, LucideIcon> = {
  "/": LayoutDashboard,
  "/predictions": Target,
  "/predictions/today": CalendarClock,
  "/high-odds": Flame,
  "/safe-bets": Shield,
  "/ai-predictions": Sparkles,
  "/jackpot-predictions": Coins,
  "/analysis": BarChart3,
  "/performance": TrendingUp,
  "/track-record": Trophy,
  "/match-preview": Eye,
  "/fixtures": History,
  "/historical-odds": History,
  "/leagues": Trophy,
  "/competitions": Trophy,
  "/countries": Compass,
  "/markets": Goal,
  "/players": Users,
  "/explore": Compass,
  "/guides": BookOpen,
  "/blog": Newspaper,
  "/how-it-works": Info,
  "/faq": HelpCircle,
  "/member": Users,
  "/responsible-gambling": ShieldAlert,
  "/about": Brain,
  "/contact": Mail,
  "/docs/mcp": Plug,
  "/settings": Settings2,
};
